import React, { useState, useContext } from "react";
import {
  Box,
  useTheme,
  IconButton,
  Button,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import axios from "axios";
import { AuthContext } from "../contexts/AuthContext";
import { useColourMode } from "../contexts/ColourContext";
import { categoryTypes } from "../data/database";

const titleCase = (str) => {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const NewProduct = ({ userId, dataUrl, header, types }) => {
  const [products, setProducts] = useState([
    {
      category: "",
      type: "",
      product: "",
      price: "",
    },
  ]);
  const [message, setMessage] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [isCustomType, setIsCustomType] = useState(false);

  const colourTheme = useTheme();
  const { accentColour } = useColourMode();
  const { isLoggedIn } = useContext(AuthContext);

  const [buttonStyle, setButtonStyle] = useState({
    backgroundColor: accentColour,
  });

  const addProductField = () => {
    setProducts([
      ...products,
      {
        category: "",
        type: "",
        product: "",
        price: "",
      },
    ]);
  };

  const removeProductField = (index) => {
    const newProducts = [...products];
    newProducts.splice(index, 1);
    setProducts(newProducts);
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    const newProducts = [...products];

    if (name === "category") {
      newProducts[index] = {
        ...newProducts[index],
        category: value,
        type: "", // Reset type when category changes
      };
    } else {
      newProducts[index] = {
        ...newProducts[index],
        [name]: value,
      };
    }

    setProducts(newProducts);
  };

  const handleTypeChange = (e, index) => {
    const { value } = e.target;
    const newProducts = [...products];

    if (value === "Other") {
      setIsCustomType(true);
      newProducts[index] = { ...newProducts[index], type: "" };
    } else {
      setIsCustomType(false);
      newProducts[index] = { ...newProducts[index], type: value };
    }

    setProducts(newProducts);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setButtonDisabled(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setMessage("Authorization token missing");
        return;
      }

      // Validate all products
      const isValid = products.every(
        (p) => p.category && p.type && p.product && p.price
      );

      if (!isValid) {
        throw new Error("All fields are required for each product");
      }

      // Send each product individually
      for (const product of products) {
        await axios.post(
          `${dataUrl}/products/${userId}`,
          {
            category: product.category,
            type: product.type,
            product: product.product,
            price: product.price,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      setMessage(`${products.length} PRODUCTS ADDED`);
      setButtonStyle({ backgroundColor: "green" });
      window.location.reload();
    } catch (error) {
      console.error("Error:", error);
      setMessage(
        error.response?.data?.error || error.message || "FAILED. TRY LATER"
      );
      setButtonStyle({ backgroundColor: "red" });
    } finally {
      setTimeout(() => {
        setMessage("");
        setButtonDisabled(false);
        setButtonStyle({ backgroundColor: accentColour });
      }, 2000);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", mb: "0.5rem" }}>
        <Box sx={{ fontSize: "1.25rem" }}>{header}</Box>
      </Box>
      <Box
        sx={{
          backdropFilter: "blur(1rem)",
          boxShadow: "0 0 1rem rgba(0, 0, 0, 0.2)",
          width: "100%",
          border: `1px solid ${colourTheme.palette.text.primary}`,
          borderRadius: "1rem",
          m: "auto",
          mb: 2.5,
          p: 1,
          height: "auto",
        }}
      >
        <form onSubmit={handleSubmit}>
          {products.map((product, index) => (
            <Box
              key={index}
              sx={{ mb: 3, borderBottom: "1px solid #ccc", pb: 2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ fontSize: "1rem" }}>Product #{index + 1}</Box>
                {index > 0 && (
                  <IconButton
                    onClick={() => removeProductField(index)}
                    size="small"
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {/* Category Dropdown */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  m: "0.5rem",
                }}
              >
                <label style={{ fontSize: "0.9rem", width: "25%" }}>
                  Category
                </label>
                <Select
                  name="category"
                  value={product.category}
                  onChange={(e) => handleInputChange(e, index)}
                  required
                  sx={{
                    width: "70%",
                    fontSize: "0.9rem",
                    background: colourTheme.palette.background.default,
                    color: colourTheme.palette.text.primary,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: colourTheme.palette.text.primary,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                        background: colourTheme.palette.background.default,
                      },
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Category
                  </MenuItem>
                  <MenuItem value="drink">Drink</MenuItem>
                  <MenuItem value="food">Food</MenuItem>
                </Select>
              </Box>

              {/* Type Dropdown */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  m: "0.5rem",
                }}
              >
                <label style={{ fontSize: "0.9rem", width: "25%" }}>Type</label>
                {!isCustomType ? (
                  <Select
                    name="type"
                    value={product.type}
                    onChange={(e) => handleTypeChange(e, index)}
                    required
                    sx={{
                      width: "70%",
                      fontSize: "0.9rem",
                      background: colourTheme.palette.background.default,
                      color: colourTheme.palette.text.primary,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: colourTheme.palette.text.primary,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 400,
                          background: colourTheme.palette.background.default,
                        },
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select Type
                    </MenuItem>
                    {product.category &&
                      types[product.category].map((type, typeIndex) => (
                        <MenuItem key={typeIndex} value={type}>
                          {titleCase(type)}
                        </MenuItem>
                      ))}
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                ) : (
                  <TextField
                    type="text"
                    name="type"
                    placeholder="Enter New Type"
                    value={product.type.toLocaleLowerCase()}
                    onChange={(e) => handleInputChange(e, index)}
                    sx={{
                      width: "70%",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: colourTheme.palette.text.primary,
                        },
                        "&:hover fieldset": {
                          borderColor: colourTheme.palette.text.primary,
                        },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "0.9rem",
                        color: colourTheme.palette.text.primary,
                      },
                    }}
                    size="small"
                  />
                )}
              </Box>

              {/* Product Name */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  m: "0.5rem",
                }}
              >
                <label style={{ fontSize: "0.9rem", width: "25%" }}>
                  Product
                </label>
                <TextField
                  type="text"
                  name="product"
                  value={product.product}
                  onChange={(e) => handleInputChange(e, index)}
                  required
                  sx={{
                    width: "70%",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: colourTheme.palette.text.primary,
                      },
                      "&:hover fieldset": {
                        borderColor: colourTheme.palette.text.primary,
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.9rem",
                      color: colourTheme.palette.text.primary,
                    },
                  }}
                  size="small"
                />
              </Box>

              {/* Price Input */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  m: "0.5rem",
                }}
              >
                <label style={{ fontSize: "0.9rem", width: "25%" }}>
                  Price
                </label>
                <TextField
                  type="number"
                  name="price"
                  value={product.price}
                  onChange={(e) => handleInputChange(e, index)}
                  required
                  sx={{
                    width: "70%",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: colourTheme.palette.text.primary,
                      },
                      "&:hover fieldset": {
                        borderColor: colourTheme.palette.text.primary,
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.9rem",
                      color: colourTheme.palette.text.primary,
                    },
                  }}
                  size="small"
                />
              </Box>
            </Box>
          ))}

          <Box
            sx={{ width: "100%", display: "grid", placeItems: "center", mt: 2 }}
          >
            <Button
              variant="outlined"
              onClick={addProductField}
              startIcon={<AddIcon />}
              sx={{
                fontSize: "0.8rem",
                width: "60%",
                padding: "0.75rem 1.5rem",
              }}
            >
              New Product
            </Button>
          </Box>

          {/* Submit Button */}
          <Box
            sx={{ width: "100%", display: "grid", placeItems: "center", mt: 2 }}
          >
            <Button
              type="submit"
              disabled={buttonDisabled}
              sx={{
                width: "60%",
                marginBottom: "0.5rem",
                padding: "0.75rem 1.5rem",
                backgroundColor: accentColour,
                color: colourTheme.palette.text.primary,
                fontSize: "0.9rem",
                textTransform: "uppercase",
                "&:hover": {
                  backgroundColor: accentColour,
                },
                ...buttonStyle,
              }}
            >
              {message ||
                `Add ${products.length} Product${
                  products.length > 1 ? "s" : ""
                }`}
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default NewProduct;
